/**
 * User API Client
 *
 * Handles all user-related API calls
 * Follows: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { Make_Authorized_API_Call } from '../services/api/utils';
import { API_ENDPOINTS } from '../services/api/endpoints';

/**
 * API Response Format (from backend)
 */
export interface APIResponse<T> {
  success: boolean;
  message: string;
  data: {
    Records: T[];
  };
  meta: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
    [key: string]: any;
  };
  error?: {
    code: string;
    details: any;
  };
}

/**
 * User interface matching backend schema
 */
export interface User {
  Email_Address: string;
  Client_ID?: string;
  User_Display_Name?: string;
  First_Name?: string;
  Last_Name?: string;
  Super_Admin: boolean;
  User_Status: string;
  Create_Date?: string;
  Last_Login_Date?: string;
  active?: boolean;
  cognito_sub?: string;
}

/**
 * User Entitlement interface
 */
export interface UserEntitlement {
  Email_Address: string;
  Client_ID: string;
  Company_ID: string | null;
  Admin: boolean;
  Approver: boolean;
  Executive: boolean;
  Granted_At?: string;
  Expires_At?: string | null;
  Client_Name?: string;
  Client_Type?: string;
  Company_Name?: string;
}

/**
 * Get user details
 * GET /api/admin/user?Email_Address=X
 */
export async function Get_User(params: { Email_Address: string }): Promise<APIResponse<User>> {
  console.log('[Get_User] Calling API for:', params.Email_Address);

  const response = await Make_Authorized_API_Call<any>(
    API_ENDPOINTS.ADMIN.USER,
    'GET',
    undefined,
    { params: { Email_Address: params.Email_Address } }
  );

  console.log('[Get_User] API response:', {
    success: response.success,
    hasData: !!response.data,
    hasNestedData: !!response.data?.data,
    message: response.message,
    dataKeys: response.data ? Object.keys(response.data) : []
  });

  // response.data contains the entire API response
  // API structure: { success, message, data: { Records: [...] }, meta: {...} }
  if (response.success && response.data && response.data.data) {
    console.log('[Get_User] Success - returning user data');
    return {
      success: true,
      message: response.data.message || 'User retrieved successfully',
      data: {
        Records: response.data.data.Records || []
      },
      meta: response.data.meta || {
        Total_Records: 0,
        Request_ID: '',
        Timestamp: new Date().toISOString()
      }
    };
  }

  console.error('[Get_User] Failed - response:', JSON.stringify(response, null, 2));
  return {
    success: false,
    message: response.message || 'Failed to get user',
    data: { Records: [] },
    meta: {
      Total_Records: 0,
      Request_ID: '',
      Timestamp: new Date().toISOString()
    },
    error: {
      code: 'USER_GET_FAILED',
      details: response.message
    }
  };
}

/**
 * Get user entitlements
 * GET /api/admin/user-entitlements?Email_Address=X
 */
export async function Get_User_Entitlements(params: { Email_Address: string }): Promise<APIResponse<UserEntitlement>> {
  const response = await Make_Authorized_API_Call<any>(
    API_ENDPOINTS.ADMIN.USER_ENTITLEMENTS,
    'GET',
    undefined,
    { params: { Email_Address: params.Email_Address } }
  );

  // response.data contains the entire API response
  // API structure: { success, message, data: { Records: [...] }, meta: {...} }
  if (response.success && response.data && response.data.data) {
    return {
      success: true,
      message: response.data.message || 'Entitlements retrieved successfully',
      data: {
        Records: response.data.data.Records || []
      },
      meta: response.data.meta || {
        Total_Records: 0,
        Request_ID: '',
        Timestamp: new Date().toISOString()
      }
    };
  }

  return {
    success: false,
    message: response.message || 'Failed to get entitlements',
    data: { Records: [] },
    meta: {
      Total_Records: 0,
      Request_ID: '',
      Timestamp: new Date().toISOString()
    },
    error: {
      code: 'ENTITLEMENTS_GET_FAILED',
      details: response.message
    }
  };
}

/**
 * Helper to check if API response is successful
 */
export function isSuccessResponse<T>(response: APIResponse<T>): boolean {
  return response.success === true;
}

// Export as userApi object for Tim-Combo pattern compatibility
export const userApi = {
  Get_User,
  Get_User_Entitlements
};
